import { formatDate, formatMonthYear } from "@/composables/formatUtils";
import type {
  CancellationRuleWarning,
  DirectCancellationStatus,
  OriginRestrictionWarning,
  ValidityWarning,
} from "@/dashboard/inventory_management/cancel/cancellationRulesTypes";
import type { CancellationTargetLookup } from "@/dashboard/inventory_management/common/cancellationTargets";
import { translateCountryCode, type TranslationFn } from "@/utils/countries";

/** A message's key, and the key of its sentence for an "Any AIB" statement, which names the route as well. */
interface AnyAibAwareKeys {
  standard: string;
  anyAib: string;
}

const SCHEME_MISMATCH_KEYS: AnyAibAwareKeys = {
  standard: "cancellation_rules.warnings.scheme_mismatch",
  anyAib: "cancellation_rules.warnings.scheme_mismatch_any_aib",
};

const UNKNOWN_SCHEME_KEYS: AnyAibAwareKeys = {
  standard: "cancellation_rules.warnings.unknown_scheme",
  anyAib: "cancellation_rules.warnings.unknown_scheme_any_aib",
};

const ORIGIN_RESTRICTION_KEYS: Record<
  OriginRestrictionWarning["stage"],
  AnyAibAwareKeys
> = {
  import: {
    standard: "cancellation_rules.warnings.origin_restriction_import",
    anyAib: "cancellation_rules.warnings.origin_restriction_import_any_aib",
  },
  disclosure: {
    standard: "cancellation_rules.warnings.origin_restriction_disclosure",
    anyAib: "cancellation_rules.warnings.origin_restriction_disclosure_any_aib",
  },
};

const anyAibAwareKey = (keys: AnyAibAwareKeys, isAnyAib: boolean) =>
  isAnyAib ? keys.anyAib : keys.standard;

const DIRECT_CANCELLATION_KEYS: Record<DirectCancellationStatus, string> = {
  // The copy of the pending-agent cancellation option, so the cancel tab and bulk-buy say the same (DEV-626 spec).
  not_available_yet: "bulk_buy.cancellation_option_agent",
  supplier_required: "cancellation_rules.warnings.supplier_required",
  not_allowed: "cancellation_rules.warnings.not_allowed",
};

/** By the date that is close, then by whether some GO's date has already passed. */
const VALIDITY_KEYS: Record<
  ValidityWarning["date_kind"],
  { close: string; passed: string }
> = {
  expiry: {
    close: "cancellation_rules.warnings.expiry_close",
    passed: "cancellation_rules.warnings.expiry_close_passed",
  },
  transfer_deadline: {
    close: "cancellation_rules.warnings.transfer_deadline_close",
    passed: "cancellation_rules.warnings.transfer_deadline_close_passed",
  },
};

/**
 * A rule warning as shown to the user.
 *
 * @property details - Further lines under the message, such as the mismatching months
 */
export interface FormattedRuleWarning {
  message: string;
  details: string[];
}

export interface RuleMessageContext {
  t: TranslationFn;
  locale: string;
  targets: CancellationTargetLookup;
}

/** One statement's warnings, named by `label` wherever a warning lists the statements it concerns. */
export interface StatementRuleWarnings {
  key: string;
  label: string;
  warnings: CancellationRuleWarning[];
}

/**
 * A warning as shown, with every statement it reads the same for.
 *
 * @property key - The formatted text, so it is stable for as long as the warning reads the same
 */
export interface RuleWarningGroup extends FormattedRuleWarning {
  key: string;
  statements: { key: string; label: string }[];
}

/**
 * Translates a warning the backend matched. The backend sends only codes and raw parameters; the translation
 * keys, regions, countries, dates and months are all chosen here.
 */
export const formatRuleWarning = (
  warning: CancellationRuleWarning,
  context: RuleMessageContext
): FormattedRuleWarning => {
  const { t } = context;
  const regionLabel = (region: string) => context.targets.label(region, t);
  const countryList = (countries: string[]) =>
    countries.map((country) => translateCountryCode(country, t)).join(", ");
  const message = (key: string, params: Record<string, unknown> = {}) => ({
    message: t(key, params),
    details: [],
  });

  switch (warning.rule) {
    case "scheme_mismatch":
      return message(anyAibAwareKey(SCHEME_MISMATCH_KEYS, warning.is_any_aib), {
        target_region: regionLabel(warning.target_region),
        target_scheme: warning.target_scheme,
        certificate_schemes: warning.certificate_schemes.join(", "),
      });
    case "unknown_scheme":
      return message(anyAibAwareKey(UNKNOWN_SCHEME_KEYS, warning.is_any_aib), {
        target_region: regionLabel(warning.target_region),
        certificate_countries: countryList(warning.certificate_countries),
        mwh: warning.mwh,
      });
    case "origin_restriction":
      return message(
        anyAibAwareKey(
          ORIGIN_RESTRICTION_KEYS[warning.stage],
          warning.is_any_aib
        ),
        {
          target_region: regionLabel(warning.target_region),
          issuing_countries: countryList(warning.issuing_countries),
        }
      );
    case "direct_cancellation": {
      const { target_region, status } = warning;
      return message(DIRECT_CANCELLATION_KEYS[status], {
        target_region: regionLabel(target_region),
        operator:
          context.targets.target(target_region)?.operator ??
          regionLabel(target_region),
      });
    }
    case "validity": {
      const keys = VALIDITY_KEYS[warning.date_kind];
      return message(warning.any_passed ? keys.passed : keys.close, {
        mwh: warning.mwh,
        threshold_days: warning.threshold_days,
        earliest_date: formatDate(warning.earliest_date),
      });
    }
    case "no_registry_account":
      return message("cancellation_rules.warnings.no_registry_account", {
        mwh: warning.mwh,
      });
    case "monthly_matching": {
      const {
        period_start,
        period_end,
        period_is_single_month,
        production_months,
      } = warning;
      return {
        message: t("cancellation_rules.warnings.monthly_matching", {
          target_region: regionLabel(warning.target_region),
        }),
        details: [
          ...(period_is_single_month
            ? []
            : [
                t(
                  "cancellation_rules.warning_details.period_not_single_month",
                  {
                    period_start: formatDate(period_start),
                    period_end: formatDate(period_end),
                  }
                ),
              ]),
          ...(production_months.length > 0
            ? [
                t("cancellation_rules.warning_details.production_months", {
                  months: production_months
                    .map((month) => formatMonthYear(month, context.locale))
                    .join(", "),
                }),
              ]
            : []),
        ],
      };
    }
  }
};

/**
 * Formats every statement's warnings and merges those that read the same into one group, naming each statement
 * once, in the statements' order.
 */
export const groupRuleWarnings = (
  statements: StatementRuleWarnings[],
  context: RuleMessageContext
): RuleWarningGroup[] => {
  const groups = new Map<string, RuleWarningGroup>();
  for (const statement of statements) {
    for (const warning of statement.warnings) {
      const formatted = formatRuleWarning(warning, context);
      const key = JSON.stringify(formatted);
      const group = groups.get(key) ?? { key, ...formatted, statements: [] };
      if (!group.statements.some(({ key }) => key === statement.key)) {
        group.statements.push({ key: statement.key, label: statement.label });
      }
      groups.set(key, group);
    }
  }
  return [...groups.values()];
};
